import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, MapPinned, Building2, Layers, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SCOPES = [
  { v: "province", label: "Seluruh Provinsi" },
  { v: "city", label: "Kota/Kabupaten" },
  { v: "district", label: "Kecamatan" },
];

export default function DistributorCoverage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("distributor:manage");
  const [areas, setAreas] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ distributor_outlet_id: "", coverage_scope: "district", province: "", city: "", district: "" });
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([api.get("/distributor-areas"), api.get("/regions", { params: { type: "province" } })]);
      setAreas(a.data.data.areas); setDistributors(a.data.data.distributors); setProvinces(p.data.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.province) { setCities([]); return; }
    api.get("/regions", { params: { type: "city", province: form.province } }).then((r) => setCities(r.data.data));
  }, [form.province]);
  useEffect(() => {
    if (!form.province || !form.city) { setDistricts([]); return; }
    api.get("/regions", { params: { type: "district", province: form.province, city: form.city } }).then((r) => setDistricts(r.data.data));
  }, [form.province, form.city]);

  const submit = async () => {
    if (!form.distributor_outlet_id) { toast.error("Pilih distributor"); return; }
    if (!form.province) { toast.error("Pilih provinsi"); return; }
    if (form.coverage_scope !== "province" && !form.city) { toast.error("Pilih kota/kabupaten"); return; }
    if (form.coverage_scope === "district" && !form.district) { toast.error("Pilih kecamatan"); return; }
    setSaving(true);
    try {
      await api.post("/distributor-areas", {
        distributor_outlet_id: Number(form.distributor_outlet_id),
        coverage_scope: form.coverage_scope,
        province: form.province,
        city: form.coverage_scope === "province" ? "*" : form.city,
        district: form.coverage_scope === "district" ? form.district : "*",
      });
      toast.success("Wilayah coverage ditambahkan");
      setForm({ distributor_outlet_id: form.distributor_outlet_id, coverage_scope: "district", province: "", city: "", district: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Hapus coverage ${a.distributor_name} — ${a.province}?`)) return;
    try { await api.delete(`/distributor-areas/${a.id}`); toast.success("Coverage dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const scopeText = (a) => a.coverage_scope === "province" ? a.province
    : a.coverage_scope === "city" ? `${a.city}, ${a.province}` : `${a.district}, ${a.city}`;

  return (
    <div data-testid="distributor-page">
      <div className="section-head compact">
        <div><p className="eyebrow">COVERAGE</p><h2>Wilayah Distributor</h2><p>Kelola wilayah eksklusif tiap distributor (tanpa tumpang tindih).</p></div>
      </div>

      {!canManage && (
        <div className="connector-alert"><ShieldAlert /><div><strong>Mode lihat saja</strong><span>Role Anda tidak dapat mengubah coverage distributor.</span></div></div>
      )}

      <div className="rj-two-col">
        {canManage && (
          <div className="panel" data-testid="coverage-form">
            <div className="panel-head"><div><h3><Plus size={15} style={{ verticalAlign: -2 }} /> Tambah Coverage</h3><p>Pilih distributor & wilayah</p></div></div>
            <div className="rj-field" style={{ marginBottom: 12 }}>Distributor
              <select className="rj-select" value={form.distributor_outlet_id} onChange={(e) => setForm({ ...form, distributor_outlet_id: e.target.value })} data-testid="coverage-distributor">
                <option value="">— Pilih distributor —</option>
                {distributors.map((d) => <option key={d.odoo_customer_id} value={d.odoo_customer_id}>{d.name}</option>)}
              </select>
              {distributors.length === 0 && <small style={{ color: "#b8791a" }}>Belum ada customer bertipe Distributor. Ubah tipe customer menjadi "Distributor" dulu.</small>}
            </div>
            <div className="rj-field" style={{ marginBottom: 12 }}>Cakupan
              <select className="rj-select" value={form.coverage_scope} onChange={(e) => setForm({ ...form, coverage_scope: e.target.value })} data-testid="coverage-scope">
                {SCOPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div className="rj-field" style={{ marginBottom: 12 }}>Provinsi
              <select className="rj-select" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value, city: "", district: "" })} data-testid="coverage-province">
                <option value="">— Pilih provinsi —</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {form.coverage_scope !== "province" && (
              <div className="rj-field" style={{ marginBottom: 12 }}>Kota/Kabupaten
                <select className="rj-select" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, district: "" })} disabled={!form.province} data-testid="coverage-city">
                  <option value="">— Pilih kota —</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {form.coverage_scope === "district" && (
              <div className="rj-field" style={{ marginBottom: 12 }}>Kecamatan
                <select className="rj-select" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} disabled={!form.city} data-testid="coverage-district">
                  <option value="">— Pilih kecamatan —</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
            <button className="rj-btn rj-btn-primary" style={{ width: "100%" }} onClick={submit} disabled={saving} data-testid="submit-coverage-btn">
              {saving ? <Loader2 size={16} className="spin" /> : <><Plus size={16} /> Tambah Wilayah</>}
            </button>
          </div>
        )}

        <div className="panel" style={{ gridColumn: canManage ? "auto" : "1 / -1" }}>
          <div className="panel-head"><div><h3><Layers size={15} style={{ verticalAlign: -2 }} /> Coverage Aktif</h3><p>{areas.length} wilayah terdaftar</p></div></div>
          {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : areas.length === 0 ? (
            <div className="rj-empty"><MapPinned /><p>Belum ada wilayah coverage.</p></div>
          ) : (
            <div className="coverage-list">
              {areas.map((a) => (
                <div className="coverage-item" key={a.id} data-testid={`coverage-row-${a.id}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 4px", borderBottom: "1px solid #f0ecea" }}>
                  <span className="priority-icon amber" style={{ flex: "0 0 auto" }}><Building2 /></span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ display: "block", fontSize: 14 }}>{a.distributor_name}</strong>
                    <small style={{ color: "#7b7470" }}><MapPinned size={11} style={{ verticalAlign: -1 }} /> {scopeText(a)}</small>
                  </div>
                  <span className="rj-pill rj-pill-mock" style={{ background: "#e8f3ee", color: "#044f31", border: "1px solid #bfe0d0" }}>
                    {SCOPES.find((s) => s.v === a.coverage_scope)?.label}
                  </span>
                  {canManage && (
                    <button className="rj-btn rj-btn-danger rj-btn-sm" onClick={() => remove(a)} data-testid={`delete-coverage-${a.id}`}><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
